import {
  Rocket,
  FlaskConical,
  PlayCircle,
  BarChart3,
  Sparkles,
  FolderKanban,
  Users,
  User,
  Wand2,
  ClipboardList,
  GitBranch,
  Shield,
  Calendar,
  Bug,
  Layers,
  Radar,
  Inbox,
  Home,
  Database,
  Download,
  Paperclip,
  Wrench,
  CalendarClock,
  type LucideIcon,
} from "lucide-react";

import {
  DEMO_CYCLE_NAME,
  DEMO_TEST_CASE_NAMES,
  DEMO_TEST_DATA_NAME,
  DEMO_REQUIREMENT_NAME,
  DEMO_DEFECT_TITLE,
  DEMO_APP_URL,
  DEMO_PROJECT_NAME,
  GUIDE_ME_TAG,
} from "./tourSeedService";

export type Difficulty = "Beginner" | "Intermediate" | "Advanced";
export type Role = "Tester" | "Lead" | "Manager" | "Developer" | "Automation" | "Owner";
export type Badge = "New" | "Popular" | "Recommended" | "Required" | "Updated";

/**
 * If set, the user must have this permission to start the tour.
 * Tours that require permissions the user lacks will show a lock icon
 * and a disabled Start button with an explanatory tooltip.
 */
export type TourPermission = {
  resource: string;
  action: string;
  /** Human-readable label shown in the lock tooltip, e.g. "Project Admin access" */
  label: string;
};

export type Tour = {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: LucideIcon;
  /** Real in-app route this tour teaches — used to deep-link into the product. */
  route: string;
  duration: number; // minutes
  steps: number;
  difficulty: Difficulty;
  roles: Role[];
  badges?: Badge[];
  progress: number; // 0-100
  completed?: boolean;
  updatedAt?: string;
  /** Key takeaways shown in the right detail panel. */
  learn?: string[];
  /**
   * Permission required to start this tour. If set and the user lacks it,
   * the Start button is disabled with a tooltip explaining what's needed.
   */
  requiredPermission?: TourPermission;
};

export type Category = {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  accent: string; // tailwind color class fragment
};

export const CATEGORIES: Category[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    description: "Set up your workspace, invite the team, and configure your first project.",
    icon: Rocket,
    accent: "from-sky-500/15 to-indigo-500/10 text-sky-600",
  },
  {
    id: "test-design",
    title: "Test Design",
    description: "Author test cases, reusable elements, test data, and requirement coverage.",
    icon: FlaskConical,
    accent: "from-violet-500/15 to-fuchsia-500/10 text-violet-600",
  },
  {
    id: "execution",
    title: "Execution",
    description: "Plan cycles, run your queue, monitor executions, and fix defects.",
    icon: PlayCircle,
    accent: "from-emerald-500/15 to-teal-500/10 text-emerald-600",
  },
  {
    id: "cycle",
    title: "Cycle",
    description:
      "Everything inside a test cycle — Overview, Scope, Assignments, Execution, Reports and Defects.",
    icon: Calendar,
    accent: "from-rose-500/15 to-pink-500/10 text-rose-600",
  },
  {
    id: "reports",
    title: "Reports",
    description: "Cycle, release, coverage, readiness and risk analytics.",
    icon: BarChart3,
    accent: "from-amber-500/15 to-orange-500/10 text-amber-600",
  },
  {
    id: "ai",
    title: "AI",
    description: "AI Sync, generation, self-healing and smart suggestions.",
    icon: Sparkles,
    accent: "from-cyan-500/15 to-blue-500/10 text-cyan-600",
  },
];

export const TOURS: Tour[] = [
  // ── Getting Started ──────────────────────────────────────────────
  {
    id: "create-project",
    title: "Create your first project",
    description: "Spin up a project, workspace, and invite your first collaborators.",
    category: "getting-started",
    icon: FolderKanban,
    route: "/settings/projects",
    duration: 4,
    steps: 4,
    difficulty: "Beginner",
    roles: ["Owner", "Manager", "Lead"],
    badges: ["Required", "Recommended"],
    progress: 0,
    requiredPermission: {
      resource: "PROJECTS",
      action: "WRITE",
      label: "Project management access (Manager or Owner role)",
    },
    learn: [
      "Create a workspace and name your first project",
      "Configure project settings and permissions",
      "Invite collaborators and assign their roles",
    ],
  },
  {
    id: "invite-members",
    title: "Invite members & roles",
    description: "Assign roles and control what each teammate can do across the workspace.",
    category: "getting-started",
    icon: Users,
    route: "/org-admin/users",
    duration: 4,
    steps: 4,
    difficulty: "Beginner",
    roles: ["Owner", "Manager"],
    badges: ["Recommended"],
    progress: 0,
    requiredPermission: {
      resource: "USERS",
      action: "WRITE",
      label: "User management access (Manager or Owner role)",
    },
    learn: [
      "Find the Users page under the Configuration menu",
      "Invite teammates via email and assign roles",
      "Understand the 4 role levels: Tester, Lead, Developer, Manager",
      "Change or revoke a member's role at any time",
    ],
  },
  {
    id: "tour-dashboard",
    title: "Tour your dashboard",
    description: "Read the Automation Control Center and track workflows in real time.",
    category: "getting-started",
    icon: Home,
    route: "/dashboard",
    duration: 4,
    steps: 4,
    difficulty: "Beginner",
    roles: ["Lead", "Manager", "Tester"],
    badges: ["New"],
    progress: 0,
    learn: [
      "Navigate the Automation Control Center at a glance",
      "Understand the summary cards and what each metric means",
      "Identify which workflows are active or failing right now",
    ],
  },
  {
    id: "user-profile",
    title: "Profile & Scope Preferences",
    description:
      "Manage personal profile details, assigned project roles, scope selection, and security.",
    category: "getting-started",
    icon: User,
    route: "/settings/userprofile",
    duration: 3,
    steps: 5,
    difficulty: "Beginner",
    roles: ["Tester", "Lead", "Developer", "Manager"],
    badges: ["New"],
    progress: 0,
    learn: [
      "View and update your personal contact details and phone number",
      "Check your assigned projects, role access, and primary project role",
      "Switch active scope (Project, Workspace, Version) directly from your profile",
      "Manage account security and change login password",
    ],
  },
  // ── Test Design ─────────────────────────────────────────────────
  {
    id: "create-test-case",
    title: "Create a test case",
    description: "Author expressive steps, expected results and attach requirements.",
    category: "test-design",
    icon: ClipboardList,
    route: "/testcases/all",
    duration: 5,
    steps: 6,
    difficulty: "Beginner",
    roles: ["Tester", "Lead"],
    badges: ["Popular"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CASES",
      action: "WRITE",
      label: "Test Case write access",
    },
    learn: [
      "Create a test case with title, priority and module",
      "Write step-by-step instructions with expected results",
      "Link the test case to a requirement for traceability",
    ],
  },
  {
    id: "reusable-elements",
    title: "Reusable UI elements",
    description: "Create and manage reusable selectors you can share across suites.",
    category: "test-design",
    icon: Layers,
    route: "/elements",
    duration: 4,
    steps: 6,
    difficulty: "Intermediate",
    roles: ["Tester", "Automation"],
    progress: 0,
    requiredPermission: {
      resource: "ELEMENTS",
      action: "WRITE",
      label: "Elements write access",
    },
  },
  {
    id: "test-data-profiles",
    title: "Build test data profiles",
    description: "Define fields and datasets to drive data-driven scenarios.",
    category: "test-design",
    icon: Database,
    route: "/test-data",
    duration: 4,
    steps: 5,
    difficulty: "Intermediate",
    roles: ["Tester", "Automation"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_DATA",
      action: "WRITE",
      label: "Test Data write access",
    },
  },
  {
    id: "requirements-coverage",
    title: "Requirement coverage",
    description: "Map requirements to tests and track coverage as they change.",
    category: "test-design",
    icon: GitBranch,
    route: "/test-management/requirements",
    duration: 4,
    steps: 5,
    difficulty: "Intermediate",
    roles: ["Lead", "Manager"],
    progress: 0,
    requiredPermission: {
      resource: "REQUIREMENTS",
      action: "WRITE",
      label: "Requirements write access (Lead or Manager role)",
    },
  },
  // ── Execution ───────────────────────────────────────────────────
  {
    id: "create-cycle",
    title: "Create your first cycle",
    description: "Set up a release, plan a cycle and assign test cases in minutes.",
    category: "cycle",
    icon: Calendar,
    route: "/cycles",
    duration: 8,
    steps: 8,
    difficulty: "Beginner",
    roles: ["Lead", "Tester", "Manager"],
    badges: ["Popular", "Recommended"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "WRITE",
      label: "Cycle management access (Lead or Manager role)",
    },
    learn: [
      "Structure cycles for reliable execution",
      "Attach releases and roll up readiness",
      "Balance workload across testers",
    ],
  },
  {
    id: "tour-a-cycle",
    title: "Tour a test cycle",
    description:
      "Learn every tab inside a cycle — Overview, Scope, Assignments, Execution, Reports and Defects.",
    category: "cycle",
    icon: Layers,
    route: "/cycles",
    duration: 6,
    steps: 9,
    difficulty: "Beginner",
    roles: ["Lead", "Tester", "Manager"],
    badges: ["New", "Recommended"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "READ",
      label: "Cycle read access",
    },
    learn: [
      "Open a cycle and read its Overview health snapshot",
      "Manage which test cases are in scope",
      "Assign testers and track execution progress",
      "Read the Reports and Defects tabs for a cycle",
    ],
  },
  {
    id: "cycle-tab-overview",
    title: "Cycle: Overview tab",
    description: "Read a cycle's health at a glance — progress, pass rate, done vs remaining.",
    category: "cycle",
    icon: Home,
    route: "/cycles",
    duration: 2,
    steps: 3,
    difficulty: "Beginner",
    roles: ["Lead", "Tester", "Manager"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "READ",
      label: "Cycle read access",
    },
  },
  {
    id: "cycle-tab-scope",
    title: "Cycle: Scope tab",
    description: "Choose which test cases are included in the cycle.",
    category: "cycle",
    icon: ClipboardList,
    route: "/cycles",
    duration: 2,
    steps: 4,
    difficulty: "Beginner",
    roles: ["Lead", "Manager"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "READ",
      label: "Cycle read access",
    },
  },
  {
    id: "cycle-tab-assignments",
    title: "Cycle: Assignments tab",
    description: "Assign testers and balance the workload across the team.",
    category: "cycle",
    icon: Users,
    route: "/cycles",
    duration: 2,
    steps: 4,
    difficulty: "Beginner",
    roles: ["Lead", "Manager"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "READ",
      label: "Cycle read access",
    },
  },
  {
    id: "cycle-tab-execution",
    title: "Cycle: Execution tab",
    description: "Track live results — pass, fail, blocked and not executed.",
    category: "cycle",
    icon: PlayCircle,
    route: "/cycles",
    duration: 2,
    steps: 3,
    difficulty: "Beginner",
    roles: ["Lead", "Tester", "Manager"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "READ",
      label: "Cycle read access",
    },
  },
  {
    id: "cycle-tab-reports",
    title: "Cycle: Reports tab",
    description: "Turn results into metrics — pass rate, velocity, ETA and per-tester progress.",
    category: "cycle",
    icon: BarChart3,
    route: "/cycles",
    duration: 2,
    steps: 3,
    difficulty: "Beginner",
    roles: ["Lead", "Manager"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "READ",
      label: "Cycle read access",
    },
  },
  {
    id: "cycle-tab-defects",
    title: "Cycle: Defects tab",
    description: "See every bug raised against this cycle, with status and priority.",
    category: "cycle",
    icon: Bug,
    route: "/cycles",
    duration: 2,
    steps: 3,
    difficulty: "Beginner",
    roles: ["Lead", "Tester", "Developer"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CYCLE",
      action: "READ",
      label: "Cycle read access",
    },
  },
  {
    id: "execute-my-queue",
    title: "Execute from My Queue",
    description: "Run your assigned cases step-by-step and capture evidence inline.",
    category: "execution",
    icon: Inbox,
    route: "/cycles/queue",
    duration: 5,
    steps: 6,
    difficulty: "Beginner",
    roles: ["Tester"],
    progress: 0,
    learn: [
      "Understand how test cases get attached and assigned to your queue",
      "Find and open your assigned test cases from My Queue",
      "Mark each step as Pass, Fail, or Blocked with one click",
      "Attach screenshots and log a defect without leaving the runner",
    ],
  },
  {
    id: "defect-workflow",
    title: "Defect workflow",
    description: "From raising an issue to closure, triage and retest.",
    category: "execution",
    icon: Bug,
    route: "/defects",
    duration: 4,
    steps: 5,
    difficulty: "Intermediate",
    roles: ["Tester", "Lead", "Developer"],
    progress: 0,
    requiredPermission: {
      resource: "DEFECTS",
      action: "WRITE",
      label: "Defects write access",
    },
  },
  {
    id: "fix-queue",
    title: "Fix Queue",
    description: "Developer view: find defects assigned to you and mark them fixed.",
    category: "execution",
    icon: Wrench,
    route: "/cycles/fix-queue",
    duration: 3,
    steps: 4,
    difficulty: "Beginner",
    roles: ["Developer", "Lead"],
    progress: 0,
  },
  {
    id: "schedules",
    title: "Schedule automated runs",
    description: "Set up recurring automated cycle executions on a timer.",
    category: "execution",
    icon: CalendarClock,
    route: "/cycles",
    duration: 4,
    steps: 4,
    difficulty: "Intermediate",
    roles: ["Lead", "Automation"],
    progress: 0,
  },
  // ── Reports ─────────────────────────────────────────────────────
  {
    id: "cycle-reports",
    title: "Cycle reports",
    description: "Understand pass rate, blockers and progress at a glance.",
    category: "reports",
    icon: BarChart3,
    route: "/reports",
    duration: 5,
    steps: 4,
    difficulty: "Beginner",
    roles: ["Lead", "Manager"],
    progress: 0,
    requiredPermission: {
      resource: "REPORTS",
      action: "READ",
      label: "Reports read access (Lead or Manager role)",
    },
    learn: [
      "Open a cycle report and read the pass/fail summary",
      "Identify blocking defects and their owners",
      "Export or share the report with stakeholders",
    ],
  },
  {
    id: "release-readiness",
    title: "Release readiness",
    description: "Score readiness, forecast completion and surface risk.",
    category: "reports",
    icon: Shield,
    route: "/reports",
    duration: 3,
    steps: 3,
    difficulty: "Advanced",
    roles: ["Manager", "Owner"],
    badges: ["Updated"],
    progress: 0,
    requiredPermission: {
      resource: "REPORTS",
      action: "READ",
      label: "Reports read access (Manager or Owner role)",
    },
  },
  // ── Automation ──────────────────────────────────────────────────
  {
    id: "import-export",
    title: "Import & export data",
    description: "Bring test assets in and out with structured import/export.",
    category: "getting-started",
    icon: Download,
    route: "/settings/import-export",
    duration: 11,
    steps: 11,
    difficulty: "Intermediate",
    roles: ["Automation", "Lead"],
    progress: 0,
    requiredPermission: {
      resource: "TEST_CASES",
      action: "IMPORT",
      label: "Import/Export access (Lead or Automation role)",
    },
  },
  {
    id: "manage-files",
    title: "Manage files & attachments",
    description: "Upload files to reference from your test steps and evidence.",
    category: "test-design",
    icon: Paperclip,
    route: "/uploads",
    duration: 3,
    steps: 3,
    difficulty: "Beginner",
    roles: ["Tester", "Automation"],
    progress: 0,
    requiredPermission: {
      resource: "UPLOADS",
      action: "WRITE",
      label: "File uploads access",
    },
  },
  // ── AI ──────────────────────────────────────────────────────────
  {
    id: "ai-generate-tests",
    title: "Generate tests with AI",
    description: "Turn requirements into test cases with a single prompt.",
    category: "ai",
    icon: Wand2,
    route: "/testcases/all",
    duration: 5,
    steps: 5,
    difficulty: "Beginner",
    roles: ["Tester", "Lead"],
    badges: ["New", "Popular"],
    progress: 0,
    requiredPermission: {
      resource: "AI_TEST_GEN",
      action: "WRITE",
      label: "AI Test Generation access",
    },
    learn: [
      "Describe a feature in plain English and generate test cases instantly",
      "Review, edit and approve AI-generated steps before saving",
      "Re-run generation to get alternative test scenarios",
    ],
  },
  {
    id: "ai-site-sync",
    title: "AI Site Sync",
    description: "Point AutopilotQA at your app and let AI discover elements into your library.",
    category: "ai",
    icon: Radar,
    route: "/elements",
    duration: 3,
    steps: 3,
    difficulty: "Intermediate",
    roles: ["Lead", "Automation"],
    badges: ["New"],
    progress: 0,
  },
];

export type TourStep = {
  title: string;
  description: string;
  purpose?: string;
  tip?: string;
  bestPractice?: string;
  warning?: string;
  eta: string;
  /** CSS selector for the real DOM element to spotlight (preferred). */
  selector?: string;
  /** Fallback absolute/percentage position when selector is absent. */
  target?: { top: string; left: string; width: string; height: string };
  placement?: "right" | "bottom" | "left" | "top";
  /**
   * Navigate to this route BEFORE showing this step.
   * Use to teach users "click here to get there" on the previous step,
   * then navigate on the next step.
   */
  route?: string;
  /**
   * When true, clicking "Next" programmatically clicks the selector element
   * (e.g. to open a dialog) before advancing to the next step.
   */
  autoClick?: boolean;
  /**
   * CSS selector of a close/cancel button to click when the user presses "Next",
   * before advancing to the next step. Use this to dismiss dialogs that were
   * opened by a previous autoClick step.
   * Use "escape" to dispatch a keyboard Escape event (works for any Radix dialog).
   * Example: '[data-tour="dialog-cancel"]' or "escape"
   */
  dismissOnNext?: string;
  /**
   * Pre-fill form fields when this step becomes active.
   * Map of CSS selector → value to inject. Works with React controlled inputs
   * via the native HTMLInputElement value setter + synthetic input event.
   * Example: { '[name="title"]': "Verify a student can enroll in a course" }
   */
  prefill?: Record<string, string>;
  /**
   * Dispatched as a CustomEvent named 'guideme:init-state' after the step
   * becomes active. Components can listen for this to seed React-managed state
   * that can't be set via native input setters (e.g. field arrays, dropdowns).
   * The event detail is whatever object you put here.
   */
  initState?: Record<string, any>;
};

/**
 * Per-tour step definitions keyed by Tour.id.
 * Every step should use a real [data-tour="..."] selector where one exists.
 * Falls back to a "Coming soon" placeholder in GuideTourContext for any tour not listed here.
 */
export const TOUR_STEPS: Record<string, TourStep[]> = {
  // ── Getting Started: Tour your dashboard ─────────────────────
  "tour-dashboard": [
    {
      title: "Find the Dashboard",
      description: "See the home icon at the top of the sidebar? Click it to open your Dashboard.",
      purpose:
        "This is where you check test health, active cycles, and what needs attention today.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-dashboard"]',
      placement: "right",
    },
    {
      title: "The summary section",
      description:
        "Up top you'll see pass rates, active cycles, and automation health. Quick snapshot of where things stand.",
      bestPractice: "Check this before standup so you're never caught off guard.",
      eta: "30 seconds",
      route: "/dashboard",
      selector: '[data-tour="dashboard-hero"]',
      placement: "bottom",
    },
    {
      title: "Switch between projects",
      description:
        "Use the project switcher in the top-left to jump between projects. No need to log out and back in.",
      tip: "Pin the sidebar to keep the project switcher always visible.",
      eta: "10 seconds",
      selector: '[data-tour="project-switcher"]',
      placement: "right",
    },
    {
      title: "The sidebar has everything",
      description:
        "Test cases, cycles, reports, defects... it's all one click away in the sidebar. Hover over it to expand.",
      eta: "15 seconds",
      selector: '[data-tour="sidebar-nav"]',
      placement: "right",
    },
  ],

  "user-profile": [
    {
      title: "Profile Overview & Status",
      description:
        "Here you can see your profile avatar, active status, email address, total project count, and primary project role.",
      purpose:
        "Quickly verify your account identity and assigned role permissions across the workspace.",
      eta: "10 seconds",
      selector: '[data-tour="user-profile-header"]',
      placement: "right",
    },
    {
      title: "Personal Information",
      description:
        "Update your First Name, Last Name, and optional Phone Number. Click 'Save changes' when editing.",
      purpose: "Keep your name and contact details accurate for team notifications and audit logs.",
      eta: "15 seconds",
      selector: '[data-tour="user-profile-personal-info"]',
      placement: "bottom",
    },
    {
      title: "Active Workspace Scope",
      description:
        "See your currently active Project and Workspace. Click 'Switch' to seamlessly change active scope.",
      purpose:
        "All test cases, cycles, requirements and reports in the platform adjust based on your active scope.",
      eta: "15 seconds",
      selector: '[data-tour="user-profile-active-scope"]',
      placement: "bottom",
    },
    {
      title: "Assigned Projects & Roles",
      description:
        "Review all projects you have access to, along with your specific role level (Tester, Lead, Manager, Developer).",
      purpose: "Role levels determine what actions you can perform in each project.",
      eta: "15 seconds",
      selector: '[data-tour="user-profile-projects-roles"]',
      placement: "left",
    },
    {
      title: "Account Security & Password",
      description:
        "Access the 'Change Password' button to update your credentials and keep your account secure.",
      purpose: "Regular password updates ensure your account and test data remain protected.",
      eta: "10 seconds",
      selector: '[data-tour="user-profile-security"]',
      placement: "left",
    },
  ],

  // ── Getting Started: Create your first project ───────────────
  "create-project": [
    {
      title: "Find Configuration",
      description:
        "See the cog icon at the bottom of the sidebar? That's where all settings live. Click it to open Configuration.",
      purpose: "Projects, teams, permissions, and integrations all live under this cog.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-settings"]',
      placement: "right",
    },
    {
      title: "Click '+ New Project'",
      description:
        "Hit the button to create a new project. We'll open the form for you on the next step.",
      bestPractice:
        "Use clear names like 'Mobile App v2' or 'API Services' so teammates know what it's for.",
      eta: "5 seconds",
      route: "/settings/projects",
      selector: '[data-tour="projects-new-btn"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Fill in the project form",
      description:
        "We've pre-filled a Guide Me demo project, including our sample app URL (learningmanagement.autopilotqa.com). Change any of it to your real project. Leave 'Seed with sample data' on so it's not empty. Click 'Save Project' when ready.",
      tip: "Choose Multi-app under Workspace if your project tests multiple independent apps.",
      eta: "30 seconds",
      selector: '[data-tour="create-project-form"]',
      placement: "left",
      dismissOnNext: '[data-tour="create-project-cancel"]',
      prefill: {
        'input[placeholder="Enter project name"]': DEMO_PROJECT_NAME,
        'input[placeholder="https://example.com"]': DEMO_APP_URL,
        'textarea[placeholder="Brief description of the project"]': `${GUIDE_ME_TAG} Demo project created during the Guide Me tour. Safe to delete.`,
      },
    },
    {
      title: "Switch to your new project",
      description:
        "Use the 'Active project' dropdown in the left panel to switch. Pick your new project and everything updates to show its data.",
      tip: "You can have different projects open in different browser tabs.",
      eta: "10 seconds",
      selector: '[data-tour="active-project-switcher"]',
      placement: "right",
    },
  ],

  // ── Getting Started: Invite members & roles ──────────────────
  "invite-members": [
    {
      title: "Find the Users page",
      description:
        "See the cog icon at the bottom of the sidebar? Click it, then go to Users. That's where you manage team members and their permissions.",
      purpose: "Roles control what each person can do across your projects.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-settings"]',
      placement: "right",
    },
    {
      title: "Click 'Invite User'",
      description:
        "Hit that button to open the invite form. We'll open it for you on the next step.",
      bestPractice: "Start with the least-privileged role. You can always upgrade later.",
      eta: "5 seconds",
      route: "/org-admin/users",
      selector: '[data-tour="users-invite-btn"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Fill in the invite form",
      description:
        "Enter their name and email. Pick a role: Tester (runs cases), Lead (manages cycles), Developer (views results), or Manager (full access). Then assign a project role and click Save.",
      warning: "Test Manager gives full write access to everything. Use it sparingly.",
      eta: "45 seconds",
      selector: '[data-tour="invite-dialog"]',
      placement: "left",
      prefill: {
        'input[placeholder="Enter first name"]': "Jane",
        'input[placeholder="Enter last name"]': "Doe",
        'input[placeholder="Enter email"]': "jane.doe@yourcompany.com",
      },
      dismissOnNext: "escape",
    },
    {
      title: "You're all set",
      description:
        "Now you know how to invite people and set their permissions. Close this dialog and head back to Guide Me for more tours.",
      tip: "You can always come back here to change roles or remove members.",
      eta: "10 seconds",
      selector: '[data-tour="users-invite-btn"]',
      placement: "bottom",
    },
  ],

  // ── Test Design: Create a test case ──────────────────────────
  "create-test-case": [
    {
      title: "Find Test Cases",
      description:
        "See the test case icon in the sidebar? Click it to open your test case library. All manual and automated cases live here.",
      purpose: "This is where all your manual and automated test cases live.",
      eta: "10 seconds",
      selector: '[data-tour="sidebar-test-cases"]',
      placement: "right",
    },
    {
      title: "Create a new test case",
      description: "Click the '+' button to start a new test case. We'll open the form for you.",
      eta: "5 seconds",
      route: "/testcases/all",
      selector: '[data-tour="scenarios-create-button"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Set title, type & priority",
      description:
        "We've pre-filled a sample title. Change it to something real. Then pick the Type (Manual or Automation), Priority, and Status.",
      bestPractice:
        "One scenario per test case. Start titles with 'Verify' or 'Confirm' so pass/fail is obvious.",
      eta: "45 seconds",
      selector: '[data-tour="test-case-create-dialog"]',
      placement: "left",
      prefill: {
        'input[placeholder="e.g. Checkout flow — guest user"]': DEMO_TEST_CASE_NAMES[0],
        'input[placeholder="example.com"]': DEMO_APP_URL,
        'textarea[placeholder="What does this test verify? AI uses this to suggest steps."]': `Verify a student can enroll in a course on ${DEMO_APP_URL}. The student clicks Enroll Now on a course with available seats and should receive a confirmation.`,
        'textarea[placeholder="Enter any preconditions for this test case"]':
          "Student is logged in and viewing a course with available seats",
      },
    },
    {
      title: "Write test steps",
      description:
        "Scroll down to Steps. Click '+ Add step' and enter what the user does (Action) and what should happen (Expected Result). One action per step.",
      tip: "Try 'Generate with AI' in the footer to auto-create steps from the title, then review them.",
      eta: "2 minutes",
      selector: '[data-tour="test-case-create-dialog"]',
      placement: "left",
    },
    {
      title: "Link requirements & assign reviewer",
      description:
        "Use the Requirements field to link this case to a requirement (for coverage reports). You can also assign a reviewer for an approval workflow.",
      warning: "Unlinked test cases won't show up in requirement coverage reports.",
      eta: "20 seconds",
      selector: '[data-tour="test-case-create-dialog"]',
      placement: "left",
    },
    {
      title: "Save it",
      description:
        "Click 'Write manually' to save and start adding steps, or 'Record' to capture steps from your live app. The test case is now ready to add to any cycle.",
      tip: "You can always edit steps later. Changes only affect future runs.",
      eta: "10 seconds",
      selector: '[data-tour="test-case-create-dialog"]',
      placement: "left",
      dismissOnNext: "escape",
    },
  ],

  // ── Test Design: Reusable UI elements ────────────────────────
  "reusable-elements": [
    {
      title: "Find UI Elements",
      description:
        "See the elements icon in the sidebar? Click it to open the UI Elements library where you manage reusable locators.",
      purpose:
        "Define a locator once and reuse it everywhere. Change it in one place, all tests update.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-ui-elements"]',
      placement: "right",
    },
    {
      title: "Here's your demo element",
      description:
        "We added 'Enroll Now Button (AutopilotQA Tour)' on the 'Course Page (AutopilotQA Tour)' screen. It's now open in the panel — you can see its name, screen, locator type (XPATH), and the actual selector value.",
      eta: "15 seconds",
      route: "/elements",
      selector: '[data-tour="element-detail-panel"]',
      placement: "left",
      initState: {
        type: "elements",
        elementName: "Enroll Now Button (AutopilotQA Tour)",
      },
    },
    {
      title: "Create your own element",
      description:
        "Click '+ Create element' to add a new locator manually. We'll open the form for you.",
      bestPractice:
        "Use stable IDs or data-testid attributes. Avoid CSS paths that break when the layout changes.",
      eta: "10 seconds",
      selector: '[data-tour="elements-create-button"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Fill in the element form",
      description:
        "The form is open with a sample Name and Value already filled in. Choose the Screen this element lives on, pick the locator Type, and leave Nested off unless the element sits inside an iframe. Then click Save.",
      tip: "XPATH works on any app. CSS Selector is shorter and reads more cleanly for most web pages.",
      eta: "1 minute",
      selector: '[data-tour="create-element-dialog"]',
      placement: "left",
      dismissOnNext: "escape",
      prefill: {
        'input[placeholder="Element name"]': `${GUIDE_ME_TAG} Enroll Now Button`,
        'input[placeholder="Locator value"]': '//button[contains(text(),"Enroll Now")]',
      },
    },
    {
      title: "Or record elements from your app",
      description:
        "Click 'Bulk record' to launch the Chrome extension. It captures elements directly from your live app so you don't have to type selectors manually.",
      tip: "Recording is way faster than typing selectors by hand.",
      eta: "2 minutes",
      selector: '[data-tour="elements-record-button"]',
      placement: "left",
    },
    {
      title: "Use them in test steps",
      description:
        "When writing automated test steps, search elements by name instead of typing raw selectors. If a selector ever changes, update it once here and all your tests stay working.",
      bestPractice:
        "Name elements after what they DO: 'Enroll Now Button', 'Course Search Input'. Not where they are.",
      eta: "30 seconds",
      selector: '[data-tour="elements-list"]',
      placement: "right",
    },
  ],

  // ── Test Design: Build test data profiles ────────────────────
  "test-data-profiles": [
    {
      title: "Find Test Data",
      description:
        "See the test data icon in the sidebar? Click it to open your test data profiles.",
      purpose:
        "Run the same test case against multiple datasets automatically. No copy-paste needed.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-test-data"]',
      placement: "right",
    },
    {
      title: "Your test data profiles",
      description:
        "This is your profile list. Each profile holds a set of test inputs — like usernames, passwords, and expected results — so you can run the same test case with different data automatically. Click '+ New test data' to create your own.",
      eta: "15 seconds",
      route: "/test-data",
      selector: '[data-tour="testdata-list"]',
      placement: "right",
    },
    {
      title: "Create your own profile",
      description: "Click '+ New test data' to start a new profile. We'll open it for you.",
      tip: "Each row runs the test once with different values — great for testing valid inputs, wrong passwords, empty fields all in one go.",
      eta: "1 minute",
      selector: '[data-tour="testdata-new-btn"]',
      placement: "top",
      autoClick: true,
    },
    {
      title: "Name it and add your data",
      description:
        "The profile is already named '[Guide Me] Login Data'. Three fields are filled in for you — Username, Password (encrypted), and ExpectedResult — with three login scenarios. Review the data, then click Save.",
      bestPractice: "Always include a happy path, a bad-input case, and an edge case.",
      eta: "2 minutes",
      selector: '[data-tour="testdata-create-dialog"]',
      placement: "right",
      prefill: {
        'input[placeholder="Enter test data profile name"]': DEMO_TEST_DATA_NAME,
      },
      initState: {
        type: "testdata",
        fields: ["Username", "Password", "ExpectedResult"],
        encryptedFields: ["Password"],
        datasets: [
          {
            name: "Valid login",
            values: {
              Username: "admin@learningmanagement.autopilotqa.com",
              Password: "ValidPass123!",
              ExpectedResult: "Dashboard loads",
            },
          },
          {
            name: "Wrong password",
            values: {
              Username: "admin@learningmanagement.autopilotqa.com",
              Password: "WrongPass!",
              ExpectedResult: "Invalid credentials error",
            },
          },
          {
            name: "Empty username",
            values: {
              Username: "",
              Password: "ValidPass123!",
              ExpectedResult: "Username required error",
            },
          },
        ],
      },
      dismissOnNext: "escape",
    },
    {
      title: "Link it to a test case",
      description:
        "Open any test case, switch to 'Data Driven' mode, and attach this profile. The test runs once per row automatically.",
      eta: "30 seconds",
      selector: '[data-tour="sidebar-test-cases"]',
      placement: "right",
    },
  ],

  // ── Test Design: Requirement coverage ────────────────────────
  "requirements-coverage": [
    {
      title: "Find Requirements",
      description:
        "See the requirements icon in the sidebar? Click it to open your requirements tracker and coverage map.",
      purpose:
        "Requirements traceability shows you which features have test coverage and which are at risk.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-requirements"]',
      placement: "right",
    },
    {
      title: "Here's an existing requirement",
      description:
        "Your Sample Project already has a few: 'Course Enrollment & Navigation' is in the tree on the left. Click it to see its title, description, priority, and linked test cases.",
      eta: "15 seconds",
      route: "/test-management/requirements",
      selector: '[data-tour="requirements-tree"]',
      placement: "right",
    },
    {
      title: "Add your own requirement",
      description: "Click '+ New requirement' to create one. We'll open the form for you.",
      tip: "You can also import requirements from Jira or upload a CSV.",
      eta: "30 seconds",
      selector: '[data-tour="requirement-new-btn"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Fill in the details",
      description:
        "We've pre-filled the Title and Description for you, and Priority (Medium) and Status (Draft) already have sensible defaults. Just pick a Type from the dropdown — that's the only required field left. Link test cases below to build your coverage map.",
      bestPractice: "Aim for at least 2 test cases per requirement: one positive, one negative.",
      eta: "1 minute",
      selector: '[data-tour="requirement-create-form"]',
      placement: "left",
      prefill: {
        "input[placeholder=\"e.g., 'User can reset password via email link'\"]":
          DEMO_REQUIREMENT_NAME,
        'textarea[placeholder="Describe the requirement — context, scope, expected behaviour."]': `${GUIDE_ME_TAG} Students can open a course on ${DEMO_APP_URL}, review its lessons, price and seats, and enroll with the Enroll Now button. Covers happy path, a full course, and an unauthenticated student.`,
      },
      dismissOnNext: "escape",
    },
    {
      title: "Check the coverage map",
      description:
        "Each requirement in the tree shows a coverage bar — green means fully covered, amber partial, grey untested. Anything with 0 linked cases lowers your readiness score.",
      warning: "Requirements with no linked test cases lower your release readiness score.",
      eta: "30 seconds",
      selector: '[data-tour="requirements-tree"]',
      placement: "right",
    },
  ],

  // ── Execution: Execute from My Queue ─────────────────────────
  "execute-my-queue": [
    {
      title: "Find My Queue",
      description:
        "See the cycles icon in the sidebar? Click it, then go to My Queue. That's where your assigned test cases wait for you.",
      purpose:
        "Before you can execute, someone has to create a cycle, attach cases, and assign them to you.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Your demo queue is ready",
      description:
        "We set up a demo cycle with test cases assigned to you. Your queue only shows what's assigned to you.",
      eta: "10 seconds",
      route: "/cycles/queue",
      selector: '[data-tour="cycles-my-queue"]',
      placement: "right",
    },
    {
      title: "Click a test case to run it",
      description:
        "Click any row to open the execution runner. You'll see each step with Pass / Fail / Blocked buttons.",
      tip: "Cases are sorted by priority. Start from the top to tackle critical ones first.",
      eta: "15 seconds",
      selector: '[data-tour="queue-list"]',
      placement: "top",
    },
    {
      title: "Mark each step",
      description:
        "Inside the runner, mark Pass, Fail, or Blocked for each step. If you mark Fail, you'll need to add a comment explaining what went wrong.",
      bestPractice: "Take your time. A careful run catches real bugs that rushing misses.",
      eta: "1–2 minutes",
      selector: '[data-tour="queue-list"]',
      placement: "top",
    },
    {
      title: "Log a defect inline",
      description:
        "If a step fails, click 'Log Defect' right there in the runner. It opens the defect form and links it to the exact failing step automatically.",
      tip: "Attach a screenshot while you're here. Saves you from filing it separately later.",
      eta: "1 minute",
      selector: '[data-tour="queue-list"]',
      placement: "top",
    },
    {
      title: "Complete the test",
      description:
        "Once all steps are marked, click 'Complete' to submit. The overall result is calculated for you. Your queue shrinks as you finish cases.",
      bestPractice: "If even one step is Blocked, mark the whole case Blocked. Don't guess.",
      eta: "10 seconds",
      selector: '[data-tour="queue-list"]',
      placement: "top",
    },
  ],

  // ── Execution: Tour a test cycle (every tab) ─────────────────
  "tour-a-cycle": [
    {
      title: "Find Cycles",
      description:
        "Click the Cycles icon in the sidebar. Every test cycle in your project lives here, grouped by release.",
      purpose:
        "A cycle is one planned run — it groups test cases, assigns testers, and tracks results in one place.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open a cycle",
      description:
        "We set up a demo cycle for you. Click it in the release tree to open its workspace — we'll open it now.",
      eta: "10 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-cycle-row"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "Overview — the health snapshot",
      description:
        "The Overview tab is the cycle's front page: progress, pass rate, and how many cases are done vs remaining. Check it first to see where the cycle stands.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-overview-tab"]',
      placement: "bottom",
    },
    {
      title: "Scope — which cases are in the cycle",
      description:
        "The Scope tab lists every test case included in this cycle. Add or remove cases here to decide exactly what gets tested this run.",
      tip: "Keep a cycle focused — only include cases relevant to this release or sprint.",
      eta: "25 seconds",
      selector: '[data-tour="cycle-scope-tab"]',
      placement: "bottom",
    },
    {
      title: "Assignments — who runs what",
      description:
        "The Assignments tab shows which tester owns each case. Distribute the workload so everyone knows what they're responsible for. (Automated cycles show a Configurations tab here instead.)",
      warning: "Unassigned cases won't appear in anyone's My Queue — they'll sit untouched.",
      eta: "25 seconds",
      selector: '[data-tour="cycle-assignments-tab"], [data-tour="cycle-configurations-tab"]',
      placement: "bottom",
    },
    {
      title: "Execution — run and track results",
      description:
        "The Execution tab is where results land. Each case shows Pass, Fail, Blocked or Not executed, updating live as testers work through their queues.",
      tip: "This is the tab to watch during an active test run.",
      eta: "25 seconds",
      selector: '[data-tour="cycle-execution-tab"]',
      placement: "bottom",
    },
    {
      title: "Reports — metrics for this cycle",
      description:
        "The Reports tab turns the raw results into numbers: pass/fail rate, velocity, ETA and per-tester progress. Use it to judge whether the cycle is on track.",
      eta: "25 seconds",
      selector: '[data-tour="cycle-reports-tab"]',
      placement: "bottom",
    },
    {
      title: "Defects — bugs found here",
      description:
        "The Defects tab collects every bug raised against this cycle, with status and priority, so nothing found during testing gets lost.",
      bestPractice:
        "Log defects from the execution runner so they link straight to the failing step.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-defects-tab"]',
      placement: "bottom",
    },
    {
      title: "Quick actions in the header",
      description:
        "The header buttons let you add cases, assign testers, clone the cycle, open the full report and edit details — without leaving the workspace.",
      tip: "Use Add cases and Assign to fill a new cycle fast.",
      eta: "15 seconds",
      selector: '[data-tour="cycle-assign-cases"], [data-tour="cycle-assign-testers"]',
      placement: "bottom",
    },
  ],

  // ── Cycle tab: Overview ──────────────────────────────────────
  "cycle-tab-overview": [
    {
      title: "Find Cycles",
      description: "Click the Cycles icon in the sidebar to open your cycles.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open a cycle",
      description:
        "Click a cycle in the release tree to open its workspace — we'll open the demo cycle now.",
      eta: "5 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-cycle-row"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "The Overview tab",
      description:
        "Overview is the cycle's front page. It shows overall progress, pass rate, and how many cases are done versus remaining — the fastest way to see where the cycle stands.",
      tip: "Check Overview before standup for an instant status read.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-overview-tab"]',
      placement: "bottom",
      autoClick: true,
    },
  ],

  // ── Cycle tab: Scope ─────────────────────────────────────────
  "cycle-tab-scope": [
    {
      title: "Find Cycles",
      description: "Click the Cycles icon in the sidebar to open your cycles.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open a cycle",
      description: "Click a cycle to open its workspace — we'll open the demo cycle now.",
      eta: "5 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-cycle-row"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "The Scope tab",
      description:
        "Scope lists every test case in this cycle. It defines exactly what gets tested this run — nothing outside this list is part of the cycle.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-scope-tab"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Add or remove cases",
      description:
        "Use 'Add cases' in the header to pull test cases into the cycle, or remove ones that don't apply. Keep the scope focused on this release or sprint.",
      bestPractice: "A tight, relevant scope makes results easier to read and act on.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-assign-cases"]',
      placement: "bottom",
    },
  ],

  // ── Cycle tab: Assignments ───────────────────────────────────
  "cycle-tab-assignments": [
    {
      title: "Find Cycles",
      description: "Click the Cycles icon in the sidebar to open your cycles.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open a cycle",
      description: "Click a cycle to open its workspace — we'll open the demo cycle now.",
      eta: "5 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-cycle-row"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "The Assignments tab",
      description:
        "Assignments shows which tester owns each case, so everyone knows what they're responsible for. (Automated cycles show a Configurations tab here instead.)",
      warning: "Unassigned cases won't appear in anyone's My Queue — they'll sit untouched.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-assignments-tab"], [data-tour="cycle-configurations-tab"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Assign testers",
      description:
        "Use 'Assign' in the header to distribute cases across the team. Balance the load so no one is overloaded and nothing is left unassigned.",
      tip: "Leave a little headroom for retests and unplanned defects.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-assign-testers"]',
      placement: "bottom",
    },
  ],

  // ── Cycle tab: Execution ─────────────────────────────────────
  "cycle-tab-execution": [
    {
      title: "Find Cycles",
      description: "Click the Cycles icon in the sidebar to open your cycles.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open a cycle",
      description: "Click a cycle to open its workspace — we'll open the demo cycle now.",
      eta: "5 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-cycle-row"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "The Execution tab",
      description:
        "Execution is where results land. Each case shows Pass, Fail, Blocked or Not executed, updating live as testers work through their queues. This is the tab to watch during an active run.",
      tip: "A cluster of failures often points at a broken environment, not the app.",
      eta: "25 seconds",
      selector: '[data-tour="cycle-execution-tab"]',
      placement: "bottom",
      autoClick: true,
    },
  ],

  // ── Cycle tab: Reports ───────────────────────────────────────
  "cycle-tab-reports": [
    {
      title: "Find Cycles",
      description: "Click the Cycles icon in the sidebar to open your cycles.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open a cycle",
      description: "Click a cycle to open its workspace — we'll open the demo cycle now.",
      eta: "5 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-cycle-row"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "The Reports tab",
      description:
        "Reports turns raw results into numbers: pass and fail rate, velocity, ETA, and per-tester progress. Use it to judge whether the cycle is on track to finish on time.",
      tip: "For a full shareable report, use the report button in the header.",
      eta: "25 seconds",
      selector: '[data-tour="cycle-reports-tab"]',
      placement: "bottom",
      autoClick: true,
    },
  ],

  // ── Cycle tab: Defects ───────────────────────────────────────
  "cycle-tab-defects": [
    {
      title: "Find Cycles",
      description: "Click the Cycles icon in the sidebar to open your cycles.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open a cycle",
      description: "Click a cycle to open its workspace — we'll open the demo cycle now.",
      eta: "5 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-cycle-row"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "The Defects tab",
      description:
        "Defects collects every bug raised against this cycle, with status and priority, so nothing found during testing gets lost. Testers usually log these straight from a failed step.",
      bestPractice:
        "Logging a defect from the failing step links it automatically — faster to fix.",
      eta: "20 seconds",
      selector: '[data-tour="cycle-defects-tab"]',
      placement: "bottom",
      autoClick: true,
    },
  ],

  // ── Execution: Monitor executions ────────────────────────────
  "monitor-executions": [
    {
      title: "Find Executions",
      description:
        "Click the cycles icon in the sidebar, then go to Executions. This is where you monitor all test runs in real time.",
      purpose: "Gives leads a bird's-eye view of all automation runs in progress.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Execution monitor",
      description:
        "Each row is a test run with its cycle, config, status, and progress. Click any row to see step-by-step results.",
      eta: "5 seconds",
      route: "/cycles/executions",
      selector: '[data-tour="cycles-executions"]',
      placement: "right",
    },
    {
      title: "Filter by status",
      description:
        "Use the status filter to narrow down to just Failed or In Progress runs. The list updates instantly.",
      tip: "Filter by 'Failed' to quickly find runs that need attention.",
      eta: "15 seconds",
      selector: '[data-tour="cycles-executions"]',
      placement: "right",
    },
    {
      title: "Status summary",
      description:
        "Open any run and you'll see a summary banner with pass/fail/blocked counts and overall health. A sudden spike in failures usually means the test environment is broken.",
      bestPractice:
        "See a spike in failures mid-cycle? Check the environment before re-running everything.",
      eta: "20 seconds",
      selector: '[data-tour="cycles-executions"]',
      placement: "right",
    },
    {
      title: "Re-run a failed test",
      description:
        "Inside any execution, click 'Re-execute' to queue a fresh run for that specific case. Only do this after you've fixed the underlying issue.",
      tip: "Repeated re-runs of the same bug just waste time and add noise.",
      eta: "20 seconds",
      selector: '[data-tour="cycles-executions"]',
      placement: "right",
    },
  ],

  // ── Execution: Defect workflow ────────────────────────────────
  "defect-workflow": [
    {
      title: "Find the Defects page",
      description:
        "See the bug icon in the sidebar? Click it to open your defect tracker. All bugs found during testing live here.",
      purpose: "Defects track bugs found during testing so nothing slips through the cracks.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-defects"]',
      placement: "right",
    },
    {
      title: "Here are your defects",
      description:
        "This is your defect tracker. Each defect shows status (Open, In Progress, Fixed, Retest), priority, and who it's assigned to. Defects linked to a cycle appear under that cycle — unlinked ones appear at the top.",
      eta: "5 seconds",
      route: "/defects",
      selector: '[data-tour="defect-list"]',
      placement: "right",
    },
    {
      title: "Create a new defect",
      description:
        "Click '+ Create Defect' at the bottom of the list (or the '+' icon in the header) to open the form. Give it a clear title, pick a priority, and describe what went wrong vs what should have happened.",
      bestPractice:
        "Always attach a screenshot and mention the exact step that failed. Devs will thank you.",
      eta: "1 minute",
      selector:
        '[data-tour="defect-create-btn"], [data-tour="defect-new-btn"], [data-tour="defect-list"]',
      placement: "right",
    },
    {
      title: "Fill in the details",
      description:
        "Add a Title (keep it short but clear), set Priority, and write the Steps to Reproduce. The more detail here the faster it gets fixed.",
      tip: "P0 means drop everything. Use it only for showstoppers.",
      eta: "1 minute",
      selector: '[data-tour="defect-create-form"]',
      placement: "left",
      prefill: {
        'input[placeholder="Title"]': DEMO_DEFECT_TITLE,
        'textarea[placeholder^="1. Click X"]': `1. Open ${DEMO_APP_URL}\n2. Navigate to any course page\n3. Click the 'Enroll Now' button\n4. Observe — no confirmation appears and the page does not change`,
      },
      dismissOnNext: "escape",
    },
    {
      title: "Track it through the workflow",
      description:
        "Defects move through Open → In Progress → Resolved → Verified. Each change is logged so you always have a full history.",
      warning:
        "Don't skip Verified. A resolved bug that nobody retested can come back to bite you.",
      eta: "20 seconds",
      selector: '[data-tour="defect-list"]',
      placement: "right",
    },
  ],

  // ── Reports: Cycle reports ────────────────────────────────────
  "cycle-reports": [
    {
      title: "Find Reports",
      description: "See the reports/chart icon in the sidebar? Click it to open your report hub.",
      purpose: "Reports are your single source of truth for cycle health and readiness.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-reports"]',
      placement: "right",
    },
    {
      title: "The pass/fail summary",
      description:
        "Summary cards show total cases, pass rate, blocked count, and open defects. Use Export to grab a PDF or Excel copy.",
      bestPractice: "Pass rate below 70% at end of cycle? Strong signal to delay the release.",
      eta: "30 seconds",
      route: "/reports",
      selector: '[data-tour="reports-export-btn"]',
      placement: "bottom",
    },
    {
      title: "Export the report",
      description: "Click Export to download a PDF or Excel version you can share offline.",
      tip: "Export at the end of each sprint to keep a permanent record.",
      eta: "15 seconds",
      selector: '[data-tour="reports-export-btn"]',
      placement: "bottom",
    },
    {
      title: "Share a live link",
      description:
        "Pick a release or cycle, then click Share to get a read-only link. Recipients always see the latest data, no need to resend.",
      tip: "You can password-protect shared links for external stakeholders.",
      eta: "20 seconds",
      selector: '[data-tour="reports-share-btn"], [data-tour="reports-export-btn"]',
      placement: "bottom",
    },
  ],

  // ── Reports: Release readiness ────────────────────────────────
  "release-readiness": [
    {
      title: "Find Reports",
      description:
        "See the reports icon in the sidebar? Click it. The Release Readiness dashboard lives inside Reports.",
      purpose: "This scores your project's quality gate before you ship to production.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-reports"]',
      placement: "right",
    },
    {
      title: "The readiness score",
      description:
        "This combines pass rate, open blockers, requirement coverage, and defect density into one number. Use Export to share it with stakeholders.",
      bestPractice: "Set a minimum threshold (like 85%) as a hard release gate in your process.",
      eta: "30 seconds",
      route: "/reports",
      selector: '[data-tour="reports-export-btn"]',
      placement: "bottom",
    },
    {
      title: "Find the risk areas",
      description:
        "Each component shows what's pulling the score down. Fix the lowest one first. Use Share to send a live link to your PM.",
      warning:
        "A high score with open Blocker defects is misleading. Always check the defect list too.",
      eta: "1 minute",
      selector: '[data-tour="reports-share-btn"], [data-tour="reports-export-btn"]',
      placement: "bottom",
    },
  ],

  // ── Automation: Import & export data ─────────────────────────
  "import-export": [
    {
      title: "Find Import & Export",
      description:
        "See the cog icon at the bottom of the sidebar? Click it, then go to Import / Export. That's where you back up data, migrate from other tools, or move stuff between workspaces.",
      purpose: "Back up your work, migrate from other tools, or copy data between workspaces.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-settings"]',
      placement: "right",
    },
    {
      title: "Click 'New export'",
      description:
        "Hit the 'New export' button in the top-right. The export panel has 4 sections to fill in.",
      tip: "You can also use the Export tab in the navigation strip below the stats cards.",
      eta: "5 seconds",
      route: "/settings/import-export",
      selector: '[data-tour="import-new-export"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Section 1: Test case type",
      description:
        "Pick 'Manual' for keyword-based test cases or 'Automation' for scripts. Most teams start with Manual.",
      bestPractice: "You can't mix Manual and Automation in one export. Run two if you need both.",
      eta: "10 seconds",
      selector: '[data-tour="export-type-section"]',
      placement: "right",
    },
    {
      title: "Section 2: Format",
      description:
        "Pick Excel (.xlsx, best for sharing), CSV (opens anywhere), or XML (full backup that preserves everything including steps and metadata).",
      warning:
        "XML is the only format that fully round-trips back without data loss. Use Excel/CSV for reports.",
      eta: "10 seconds",
      selector: '[data-tour="export-format-section"]',
      placement: "right",
    },
    {
      title: "Section 3: Scope",
      description:
        "Pick: Project → Workspace → Version (required, unlocks the test case list) → Folder (or leave as 'All').",
      warning: "Version is required. The export button stays disabled until you pick one.",
      eta: "30 seconds",
      selector: '[data-tour="export-scope-section"]',
      placement: "right",
    },
    {
      title: "Section 4: Select test cases",
      description:
        "All cases are pre-selected. Use search to find specific ones, or click 'None' and pick just what you need. The summary on the right updates as you go.",
      tip: "XML format skips this step and exports everything.",
      eta: "30 seconds",
      selector: '[data-tour="export-cases-section"]',
      placement: "right",
    },
    {
      title: "Review & generate",
      description:
        "Check the Export Summary panel. Make sure Type, Format, Scope, and Cases count look right. Hit 'Generate export'. Excel/CSV download instantly. XML runs in the background.",
      bestPractice:
        "Run a full XML export before any major migration or bulk delete. It's your safety net.",
      eta: "15 seconds",
      selector: '[data-tour="export-summary-panel"]',
      placement: "left",
    },
    {
      title: "Click 'New import'",
      description: "Now let's try importing. Click 'New import' and we'll open the panel for you.",
      tip: "You can edit an exported Excel/CSV and re-import it. The column format is the same.",
      eta: "5 seconds",
      selector: '[data-tour="import-new-import"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Choose source format",
      description:
        "Pick your source: 'ZIP' for an AutopilotQA archive (.zip or .xml), 'Excel' for a filled-in template, or 'CSV' for a comma-separated file.",
      tip: "If you exported Excel/CSV earlier, you can edit and re-import it directly.",
      eta: "10 seconds",
      selector: '[data-tour="import-mode-section"]',
      placement: "bottom",
    },
    {
      title: "Upload your file",
      description:
        "Drag and drop your file onto the upload zone, or click 'Browse'. A green tick appears when it's accepted. Click 'Remove' to swap it.",
      warning:
        "Only .zip and .xml files work in archive mode. Check the extension if you get an error.",
      eta: "20 seconds",
      selector: '[data-tour="import-file-section"]',
      placement: "right",
    },
    {
      title: "Set destination & run import",
      description:
        "Pick your target: Project → Workspace → Version (required) → Folder (optional). Check the Import Summary, then click 'Run import'.",
      bestPractice:
        "Import into a staging version first to verify the data before touching your live version.",
      eta: "30 seconds",
      selector: '[data-tour="import-destination-section"]',
      placement: "right",
    },
  ],

  // ── Automation: Manage files & attachments ───────────────────
  "manage-files": [
    {
      title: "Find Files",
      description: "See the files icon in the sidebar? Click it to open your file manager.",
      purpose: "Screenshots, logs, and test assets you upload during execution all live here.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-files"]',
      placement: "right",
    },
    {
      title: "Upload a file",
      description: "Click the '+' button in the top-right to add images, logs, or documents.",
      tip: "Upload a baseline screenshot before a release so you can compare after.",
      eta: "30 seconds",
      route: "/uploads",
      selector: '[data-tour="files-upload-btn"]',
      placement: "bottom",
    },
    {
      title: "Browse and preview",
      description:
        "All uploaded files show up in the list. Click any file to preview it. Use search to find files by name, or right-click for rename/delete options.",
      bestPractice:
        "Always attach a screenshot to any Fail result during execution. Makes triage way easier.",
      eta: "20 seconds",
      selector: '[data-tour="files-list"]',
      placement: "right",
    },
  ],

  // ── AI: Generate tests with AI ────────────────────────────────
  "ai-generate-tests": [
    {
      title: "Find Test Cases",
      description:
        "See the test cases icon in the sidebar? Click it. AI generation lives inside the test case workspace.",
      purpose: "Keeping test cases centralised makes them reusable across cycles and releases.",
      tip: "Use the search bar to check if a case already exists before generating new ones.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-test-cases"]',
      placement: "right",
    },
    {
      title: "Click Generate with AI",
      description:
        "Click the 'Generate with AI' button (the sparkles icon) in the toolbar. We'll open the generator for you.",
      bestPractice: "Pick the most relevant test suite first, then generate into it.",
      eta: "10 seconds",
      route: "/testcases/all",
      selector: '[data-tour="ai-generate-btn"]',
      placement: "bottom",
      autoClick: true,
    },
    {
      title: "Describe your feature",
      description:
        "In the generator, type a plain-English description of what you want to test — for example 'A student can enroll in a course' or 'Enrolling in a full course shows a waitlist message'. Then hit Generate.",
      tip: "Be specific. Clear prompts give you better test cases, and you can always generate again.",
      eta: "1 minute",
      selector: '[data-tour="ai-generator-modal"]',
      placement: "left",
    },
    {
      title: "Review what was generated",
      description:
        "The generator lists suggested test cases with titles, steps and expected results. Read through them and deselect any that don't apply before saving.",
      bestPractice: "Keep cases atomic: one scenario per case makes failures easier to diagnose.",
      eta: "2 minutes",
      selector: '[data-tour="ai-generator-modal"]',
      placement: "left",
    },
    {
      title: "Save to your suite",
      description:
        "Click Save in the generator to add the approved cases to your suite. They're ready to assign to a cycle right away.",
      tip: "You can regenerate anytime to get fresh alternatives.",
      eta: "10 seconds",
      selector: '[data-tour="ai-generator-modal"]',
      placement: "left",
      dismissOnNext: "escape",
    },
  ],

  // ── AI: AI Site Sync ──────────────────────────────────────────
  "ai-site-sync": [
    {
      title: "Find UI Elements",
      description:
        "See the UI Elements icon in the sidebar? Click it. AI Site Sync fills this library for you automatically.",
      purpose:
        "Site Sync crawls your live app and auto-discovers interactive elements so you don't have to add them by hand.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-ui-elements"]',
      placement: "right",
    },
    {
      title: "Where synced elements land",
      description:
        "This is your UI Elements library. Anything AI Site Sync discovers shows up right here, grouped by screen, ready to use in test steps.",
      tip: "You kick off a sync from Configuration → your project → the AI Sync tab, where you paste your app's URL.",
      eta: "20 seconds",
      route: "/elements",
      selector: '[data-tour="elements-list"]',
      placement: "right",
    },
    {
      title: "Start a sync from project settings",
      description:
        "Open Configuration (the cog), pick your project, and go to the AI Sync tab. Paste your app URL and run it — the crawler finds buttons, inputs and links and adds them here automatically.",
      bestPractice:
        "Rename synced elements to readable names like 'Enroll Now Button' before using them in steps.",
      eta: "2 minutes",
      route: "/elements",
      selector: '[data-tour="elements-create-button"]',
      placement: "bottom",
    },
  ],

  // ── Execution: Fix Queue ───────────────────────────────────────
  "fix-queue": [
    {
      title: "Find Fix Queue",
      description:
        "Click the Cycles icon in the sidebar, then go to Fix Queue. This is the developer's view — every defect from any cycle that's assigned to you lands here.",
      purpose:
        "Keeps developers focused: one list of everything that needs fixing, prioritised by severity.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Your assigned defects",
      description:
        "Defects are grouped by priority (P0 first). Each row shows the title, the cycle it came from, and the step where it failed. Click any defect to see full details and reproduction steps.",
      eta: "15 seconds",
      route: "/cycles/fix-queue",
      selector: '[data-tour="fix-queue-list"]',
      placement: "right",
    },
    {
      title: "Mark as Fixed",
      description:
        "Once you've pushed a fix, click 'Mark Fixed' on the defect. This changes the status to Fixed and notifies the QA tester to retest it in the next cycle run.",
      bestPractice: "Only mark Fixed after the code is actually merged — never speculatively.",
      eta: "20 seconds",
      selector: '[data-tour="fix-queue-list"]',
      placement: "right",
    },
    {
      title: "Filter by status",
      description:
        "Use the status filter at the top to switch between Open, In Progress, Fixed, and All. 'Open' is your daily to-do list.",
      tip: "Check this page at the start of each sprint — it's your personal QA signal.",
      eta: "10 seconds",
      selector: '[data-tour="fix-queue-toolbar"]',
      placement: "bottom",
    },
  ],

  // ── Execution: Schedules ──────────────────────────────────────
  schedules: [
    {
      title: "Find Cycles",
      description:
        "Click the Cycles icon in the sidebar. Schedules live inside individual automated cycles — open any automated cycle to get started.",
      purpose:
        "Schedules let you run your test suite automatically on a timer — no manual trigger needed.",
      eta: "5 seconds",
      selector: '[data-tour="sidebar-cycles"]',
      placement: "right",
    },
    {
      title: "Open the demo automated cycle",
      description:
        "We created 'AutopilotQA Demo — Automation Sprint' for you. It's highlighted in the tree — we'll click it to open the workspace.",
      tip: "Only Automated cycles support schedules. Manual cycles require a human to start them.",
      eta: "10 seconds",
      route: "/cycles",
      selector: '[data-tour="demo-automated-cycle"], [data-tour="automated-cycle-row"]',
      placement: "right",
      autoClick: true,
    },
    {
      title: "Go to the Schedules tab",
      description:
        "Inside the cycle workspace, click the Schedules tab in the navigation strip. This is where you create and manage recurring runs.",
      eta: "5 seconds",
      selector: '[data-tour="cycle-schedules-tab"]',
      placement: "bottom",
    },
    {
      title: "Create a schedule",
      description:
        "Click '+ New Schedule', pick a frequency (daily, weekly, custom cron), set the time and timezone, and save. The cycle will trigger automatically — results appear in Executions.",
      bestPractice:
        "Schedule nightly runs against your staging branch so failures are caught before the morning standup.",
      eta: "1 minute",
      selector: '[data-tour="cycle-configurations-tab"]',
      placement: "right",
    },
  ],
};

// Steps for the interactive demo (Create your first cycle)
export const DEMO_STEPS: TourStep[] = [
  {
    title: "Find Cycles",
    description:
      "See the cycles icon in the sidebar? Click it. This is where all your planned test runs live.",
    purpose: "Cycles group test cases into a run tied to a release.",
    tip: "Pin the sidebar open so it stays visible while you work.",
    eta: "5 seconds",
    selector: '[data-tour="sidebar-cycles"]',
    placement: "right",
  },
  {
    title: "Click + Cycle",
    description:
      "We'll open the Create Cycle dialog for you. You can also click '+ Cycle' in the release tree panel yourself.",
    tip: "Don't see a release folder? Create one first. Cycles need to belong to a release.",
    eta: "10 seconds",
    route: "/cycles",
    selector: '[data-tour="cycles-new-btn"]',
    placement: "bottom",
    autoClick: true,
  },
  {
    title: "Name your cycle",
    description:
      "We've pre-filled a sample name. Change it to match your sprint or release, like 'Sprint 42 Regression'.",
    bestPractice: "Include the release and sprint name so you can find it months later.",
    eta: "15 seconds",
    selector: '[data-tour="cycle-name-input"]',
    placement: "left",
    prefill: {
      '[data-tour="cycle-name-input"] input, input[data-tour="cycle-name-input"]': DEMO_CYCLE_NAME,
    },
  },
  {
    title: "Pick a release folder",
    description:
      "Select a release folder from the dropdown. We've created a demo one called '[Guide Me] Demo Release' for you.",
    warning: "A cycle without a release folder won't show up in release reports.",
    eta: "15 seconds",
    selector: '[data-tour="cycle-release-picker"]',
    placement: "left",
  },
  {
    title: "Create the cycle",
    description:
      "Click 'Create as Draft' to save it. It opens in draft mode so you can add cases before going live.",
    tip: "Draft cycles aren't visible to testers yet. Activate when you're ready.",
    eta: "10 seconds",
    selector: '[data-tour="cycle-create-submit"]',
    placement: "left",
  },
  {
    title: "Add test cases",
    description:
      "Click the + button to open the case picker. We've created some demo cases tagged [Guide Me] for you to add.",
    tip: "Search for 'Guide Me' to find them quickly.",
    eta: "1 minute",
    selector: '[data-tour="cycle-assign-cases"]',
    placement: "bottom",
  },
  {
    title: "Assign testers",
    description: "Click Assign to distribute test cases across your team.",
    bestPractice: "Leave 15% headroom for retesting and unplanned defects.",
    eta: "45 seconds",
    selector: '[data-tour="cycle-assign-testers"]',
    placement: "bottom",
  },
  {
    title: "Activate the cycle",
    description:
      "Click Activate to go live. Testers will see their queue and results start tracking.",
    tip: "You can come back to Guide Me and redo this tour anytime.",
    eta: "10 seconds",
    selector: '[data-tour="cycle-start-btn"]',
    placement: "left",
  },
];

// Wire create-cycle to its demo steps
TOUR_STEPS["create-cycle"] = DEMO_STEPS;
